import { AutocompleteResult, QFilter } from '@/types';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { DomainCode } from '@australiangreens/ag-internal-components';
import { logger } from '@/util';
import callBackendApi from './call';

export interface GroupRecord {
  id: number;
  title: string;
}

export const getGroups = async (
  token: string,
  ids: number[],
  domainCode: DomainCode,
  abortSignal?: AbortSignal
) => {
  logger.debug('getGroups() called', {});

  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/groups/?ids=${encodeURIComponent(JSON.stringify(ids))}&domainCode=${domainCode}`,
    abortSignal,
  });
  return data as AutocompleteResult[];
};

export const getGroupsContaining = async (
  token: string,
  containedString: string,
  domainCode: DomainCode,
  abortSignal?: AbortSignal
) => {
  logger.debug('getGroupsContaining() called', {});

  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/groups/containing/${encodeURIComponent(containedString)}?domainCode=${domainCode}`,
    abortSignal,
  });
  return data as GroupRecord[];
};

export const getGroupByTitle = async (
  token: string,
  groupTitle: string,
  abortSignal?: AbortSignal
) => {
  logger.debug('getGroupByTitle() called', {});

  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/groups/getbytitle/${encodeURIComponent(groupTitle)}`,
    abortSignal,
  });
  return data as GroupRecord[];
};

export const addToGroups = async (
  token: string,
  values: { domainCode: DomainCode; qFilter: QFilter; groupIds: number[] }
) => {
  logger.debug('addToGroups() called', {});

  const data = await callBackendApi({
    token,
    method: 'post',
    endpoint: '/addtogroups',
    data: values,
  });

  return data;
};

export type AddToGroupsAdvData = {
  tree: SearchTreeNode;
  addToGroupIds: number[];
  domainCode: DomainCode;
};

// [LIST-680]
export const addToGroupsAdv = async (token: string, data: AddToGroupsAdvData) =>
  callBackendApi({
    token,
    method: 'post',
    endpoint: '/addtogroups/adv',
    data,
  });

export const removeFromGroups = async (
  token: string,
  values: { qFilter: QFilter; groupIds: number[]; domainCode: DomainCode }
) => {
  logger.debug('removeFromGroups() called', {});

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: '/removefromgroups',
    data: values,
  });

  return data;
};

export type RemoveFromGroupsAdvData = {
  tree: SearchTreeNode;
  removeFromGroupIds: number[];
  domainCode: DomainCode;
};

// [LIST-680]
export const removeFromGroupsAdv = async (token: string, data: RemoveFromGroupsAdvData) =>
  callBackendApi({
    token,
    method: 'put',
    endpoint: '/removefromgroups/adv',
    data,
  });

export const createNewGroup = async (
  token: string,
  values: { domainCode: DomainCode; qFilter: QFilter; groupNames: string[] }
) => {
  logger.debug('createNewGroup() called', {});

  const data = await callBackendApi({
    token,
    method: 'post',
    endpoint: '/groups',
    data: values,
  });

  return data;
};

export type CreateNewGroupAdvData = {
  tree: SearchTreeNode;
  titles: string[];
  domainCode: DomainCode;
};

// [LIST-680]
export const createNewGroupAdv = async (token: string, data: CreateNewGroupAdvData) =>
  callBackendApi({
    token,
    method: 'post',
    endpoint: '/groups/adv',
    data,
  });

// This method takes 'values' as an argument - a sequence of AutocompleteResult
// items, which correspond to a list of Groups found in a search. It returns a
// sequence (sometimes smaller, sometimes empty) of AutocompleteResult items;
// these indicate the Groups which are valid for the current user (as indicated
// by token).

export const validateGroups = async (
  token: string,
  values: AutocompleteResult[],
  domainCode: string
) => {
  logger.debug('validateGroups() called', {});
  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/groups/validate?values=${encodeURIComponent(
      JSON.stringify(values)
    )}&domainCode=${domainCode}`,
  });
  return data as AutocompleteResult[];
};
